import React, { useState } from 'react';
import { Button, Flex, Text } from 'common/common-components.styled';
import Switch from 'react-switch';
import { useIsUserHaveRole } from 'hooks/use-is-user-have-role';
import { USER_ROLES } from '../../../constants';
import { ChooseDriverDialogProps } from './choose-driver-dialog.types';
import useDriverFields from 'components/configuration-from-pdf/hooks/use-driver-fields';
import { useForm } from 'react-hook-form';
import FormHook from '../../form-hook/form-hook';
import { ConfigurationFromPdfFieldNames } from '../../configuration-from-pdf/configuration-from-pdf.types';
import useOldCarFields from '../../configuration-from-pdf/hooks/use-old-car-fields';
import useResponsiblePerson from '../../configuration-from-pdf/hooks/use-responsible-person';
import { generateFormField } from '../../../utils/form/generate-form-fields/generate-form-fields';
import { FormSourceEnum } from '../../form-hook/form-field/hooks/use-set-options/use-set-options.props';
import { errorMessages } from '../../../utils/error-messages';
import { PAGES } from '../../../common/enums';
import { encodeBase64 } from '../../../utils/decode-encode-base64';
import { useHistory } from 'react-router-dom';
import { useTypedSelector } from '../../../hooks/use-typed-selector';
import { AnyObject, User } from '../../../common/interfaces';
import useCheckDriversCars from '../../configuration-from-pdf/hooks/use-check-drivers-cars';
import MaxConfigurationsHint from '../../configuration-from-pdf/max-configurations-hint';

const policiesFormFields = [
  generateFormField.lookup({
    label: 'Car-Policy',
    name: ConfigurationFromPdfFieldNames.CarPolicy,
    source: FormSourceEnum.GetUserCompanyPoolcarCarPolicies,
    validation: {
      required: errorMessages.requiredOptionSet
    }
  }),
  generateFormField.lookup({
    label: 'Mobilitätsbudget',
    name: ConfigurationFromPdfFieldNames.DriverLevel,
    source: FormSourceEnum.GetUserCompanyDriverLevels,
    validation: {
      required: errorMessages.requiredOptionSet
    }
  })
];

const ChooseDriverDialog = ({ closeAlert, refreshList }: ChooseDriverDialogProps) => {
  const history = useHistory();
  const user = useTypedSelector<User>(state => state['app'].user);
  const [isReplacementCar, setIsReplacementCar] = useState(false);
  const [isPoolcar, setIsPoolcar] = useState(false);

  const isPoolManager = useIsUserHaveRole(USER_ROLES.poolManager.id);
  const { checkDriversCars } = useCheckDriversCars(refreshList);

  const reactHookFormData = useForm();

  const {
    handleSubmit,
    formState: { isValid }
  } = reactHookFormData;

  const { driverFields, showMaxConfigurationsHint, setShowMaxConfigurationsHint } = useDriverFields(
    isPoolcar,
    reactHookFormData
  );
  const { oldCarFields } = useOldCarFields(isReplacementCar, reactHookFormData);
  const { responsiblePersonFields } = useResponsiblePerson(isPoolcar, isReplacementCar, reactHookFormData);
  const oldCarFieldKey = (oldCarFields[0].validation.required || 'false') as string;

  const goToNewReferenceConfigurationsPage = (data: AnyObject) => {
    history.push(`${PAGES.NEW_REFERENCE_CONFIGURATIONS}?data=${encodeBase64(JSON.stringify(data))}`);
    closeAlert();
  };

  const save = () => {
    handleSubmit(data => {
      if (isPoolManager && !data[ConfigurationFromPdfFieldNames.ResponsiblePerson]) {
        data[ConfigurationFromPdfFieldNames.ResponsiblePerson] = user.id;
      }
      if (!data[ConfigurationFromPdfFieldNames.Driver]) {
        delete data[ConfigurationFromPdfFieldNames.Driver];
      }
      if (!data[ConfigurationFromPdfFieldNames.OldCar]) {
        delete data[ConfigurationFromPdfFieldNames.OldCar];
      }

      if (isPoolcar || !data[ConfigurationFromPdfFieldNames.Driver]) {
        goToNewReferenceConfigurationsPage(data);
      } else {
        checkDriversCars({
          driverId: data[ConfigurationFromPdfFieldNames.Driver],
          successCallback: () => {
            goToNewReferenceConfigurationsPage(data);
          },
          setMaxConfigurationsNumberReached: () => setShowMaxConfigurationsHint(true)
        });
      }
    })();
  };

  return (
    <Flex direction='column' padding='15px'>
      <Text size='20' bold color='grey800' bottom='30'>
        Konfigurere ein Mitarbeiter-Fahrzeug oder ein Pool-Car.
      </Text>

      <FormHook reactHookFormData={reactHookFormData} formFields={driverFields} />

      <Flex align='center' bottom='20'>
        <Switch
          offColor={'#bfbfbf'}
          onColor={'#335566'}
          height={20}
          width={40}
          uncheckedIcon={false}
          checkedIcon={false}
          onChange={event => setIsPoolcar(event)}
          checked={isPoolcar}
        />
        <Text left='10' color='black' pointer onClick={() => setIsPoolcar(flag => !flag)}>
          Poolcar
        </Text>
      </Flex>

      {isPoolcar && <FormHook reactHookFormData={reactHookFormData} formFields={policiesFormFields} />}

      <FormHook key={oldCarFieldKey} reactHookFormData={reactHookFormData} formFields={oldCarFields} />

      <Flex align='center' bottom='20'>
        <Switch
          id='IsReplacementCarSwitcher'
          offColor={'#bfbfbf'}
          onColor={'#335566'}
          height={20}
          width={40}
          uncheckedIcon={false}
          checkedIcon={false}
          onChange={event => setIsReplacementCar(event)}
          checked={isReplacementCar}
        />
        <Text left='10' color='black' pointer onClick={() => setIsReplacementCar(flag => !flag)}>
          Dies ist kein Ersatzfahrzeug
        </Text>
      </Flex>

      {!isPoolManager && <FormHook reactHookFormData={reactHookFormData} formFields={responsiblePersonFields} />}

      {showMaxConfigurationsHint && <MaxConfigurationsHint />}

      <Flex justify='flex-end' top='30'>
        <Button onClick={closeAlert} secondary>
          Abbrechen
        </Button>
        <Button disabled={!isValid || showMaxConfigurationsHint} left='10' onClick={save}>
          Weiter
        </Button>
      </Flex>
    </Flex>
  );
};

export default ChooseDriverDialog;
